import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MessageSquare,
  Send,
  Search,
  User,
  Loader2,
  ChevronLeft,
  Phone,
  Mail,
  Tag,
  StickyNote,
  ExternalLink,
  Check,
  Pause,
  XCircle,
  Bot,
  UserCircle,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchClaw9Conversations,
  fetchClaw9Messages,
  sendClaw9Message,
  markClaw9ConversationRead,
  updateClaw9Conversation,
  updateClaw9Contact,
  lookupClaw9LeadByContact,
} from "../api/claw9";
import { getChannelLabel } from "../utils/messagingChannels";

// Channel configuration with Claw9 red theme
const CHANNELS = {
  all: { label: "All", icon: MessageSquare, color: "slate" },
  zalo: { label: "Zalo", icon: MessageSquare, color: "blue" },
  telegram: { label: "Telegram", icon: MessageSquare, color: "sky" },
  messenger: { label: "Facebook Messenger", icon: MessageSquare, color: "indigo" },
  facebook: { label: "Facebook Messenger", icon: MessageSquare, color: "indigo" },
  instagram: { label: "Instagram DM", icon: MessageSquare, color: "purple" },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "emerald" },
};

const STATUS_OPTIONS = [
  { value: "open", label: "Open", icon: Check, color: "emerald" },
  { value: "paused", label: "Paused", icon: Pause, color: "amber" },
  { value: "closed", label: "Closed", icon: XCircle, color: "slate" },
];

// Helper to format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

// Helper to format full timestamp
const formatTimestamp = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Helper to determine if AI is active based on messages
// -s pauses the bot (human takes over), -r resumes the bot
const isAIActive = (messages) => {
  if (!messages || messages.length === 0) return true; // Default: AI is active

  // Scan messages from newest to oldest to find the last -s or -r command
  for (let i = messages.length - 1; i >= 0; i--) {
    const content = messages[i]?.content?.trim();
    if (content === "-s") return false; // Human took over
    if (content === "-r") return true;  // AI resumed
  }

  return true; // Default: AI is active
};

// AI Status toggle component - clickable switch to enable/disable AI
const AIStatusToggle = ({ isActive, onToggle, isToggling }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isToggling && onToggle) {
          onToggle();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isToggling && onToggle) onToggle();
        }
      }}
      style={{
        position: 'relative',
        zIndex: 100,
        cursor: isToggling ? 'wait' : 'pointer',
        userSelect: 'none',
      }}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
        isActive
          ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
          : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
      } ${isToggling ? "opacity-50" : ""}`}
      title={isActive ? "Click to pause AI (human takeover)" : "Click to resume AI responses"}
    >
      {isToggling ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <span className="text-sm">{isActive ? "🤖" : "👤"}</span>
      )}
      <span>{isActive ? "A.I responding" : "Human mode"}</span>
      {/* Toggle switch visual */}
      <div
        className={`relative w-8 h-4 rounded-full transition-colors duration-200 ${
          isActive ? "bg-emerald-500" : "bg-amber-500"
        }`}
      >
        <div
          className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-all duration-200 ${
            isActive ? "right-0.5" : "left-0.5"
          }`}
        />
      </div>
    </div>
  );
};

// Channel badge component
const ChannelBadge = ({ channel, size = "sm" }) => {
  const config = CHANNELS[channel] || { ...CHANNELS.all, label: getChannelLabel(channel) };
  const sizeClasses = size === "sm" ? "w-4 h-4 text-[10px]" : "w-5 h-5 text-xs";
  const colorClasses = {
    blue: "bg-blue-500",
    sky: "bg-sky-500",
    indigo: "bg-indigo-500",
    purple: "bg-purple-500",
    emerald: "bg-emerald-500",
    slate: "bg-slate-500",
  };

  return (
    <div
      className={`${sizeClasses} ${colorClasses[config.color]} rounded-full flex items-center justify-center text-white font-bold`}
      title={config.label}
    >
      {config.label[0]}
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const colorClasses = {
    emerald: "bg-emerald-500/20 text-emerald-400",
    amber: "bg-amber-500/20 text-amber-400",
    slate: "bg-white/10 text-white/60",
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
};

// Conversation list item - Claw9 red theme
const ConversationItem = ({ conversation, isSelected, onClick }) => {
  const { contact_name, last_message, last_message_at, channel, unread_count, status } = conversation;

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 text-left transition-colors border-b border-white/10 hover:bg-white/5 ${
        isSelected ? "bg-[#ff4d4d]/10 border-l-2 border-l-[#ff4d4d]" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar with channel badge */}
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-white/80 font-medium">
            {contact_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="absolute -bottom-0.5 -right-0.5">
            <ChannelBadge channel={channel} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-white truncate">
              {contact_name || "Unknown"}
            </span>
            <span className="text-xs text-white/50 flex-shrink-0">
              {formatRelativeTime(last_message_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <p className="text-sm text-white/60 truncate">
              {last_message || "No messages yet"}
            </p>
            {unread_count > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 bg-[#ff4d4d] rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                {unread_count > 99 ? "99+" : unread_count}
              </span>
            )}
          </div>
          {status && status !== "open" && (
            <div className="mt-1">
              <StatusBadge status={status} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Message bubble component - Claw9 red theme
const MessageBubble = ({ message, isAgent }) => {
  const { content, sender_name, sender_type, created_at, avatar_url } = message;

  return (
    <div className={`flex gap-2 mb-3 ${isAgent ? "flex-row-reverse" : ""}`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        {avatar_url ? (
          <img
            src={avatar_url}
            alt={sender_name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              sender_type === "bot"
                ? "bg-purple-500/20 text-purple-400"
                : isAgent
                ? "bg-[#ff4d4d]/20 text-[#ff4d4d]"
                : "bg-white/10 text-white/60"
            }`}
          >
            {sender_type === "bot" ? (
              <Bot className="w-4 h-4" />
            ) : (
              <UserCircle className="w-4 h-4" />
            )}
          </div>
        )}
      </div>

      {/* Bubble */}
      <div className={`max-w-[70%] ${isAgent ? "items-end" : "items-start"}`}>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium text-white/60">
            {sender_name || (isAgent ? "Agent" : "Customer")}
          </span>
          <span className="text-[10px] text-white/40">
            {formatTimestamp(created_at)}
          </span>
        </div>
        <div
          className={`px-3 py-2 rounded-2xl ${
            isAgent
              ? "bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] text-white rounded-tr-sm"
              : "bg-white/10 text-white rounded-tl-sm"
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
        </div>
      </div>
    </div>
  );
};

// Contact sidebar component - Claw9 red theme
const ContactSidebar = ({ conversation, contact, leadLink, onUpdateNotes }) => {
  const [notes, setNotes] = useState(contact?.notes || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotes(contact?.notes || "");
  }, [contact]);

  const handleSaveNotes = async () => {
    if (!contact) return;
    setIsSaving(true);
    try {
      await onUpdateNotes(notes);
    } finally {
      setIsSaving(false);
    }
  };

  if (!conversation) return null;

  return (
    <div className="h-full w-full flex flex-col bg-[#0a0f1c]">
      {/* Contact Header */}
      <div className="p-4 border-b border-white/10 bg-[#0d1424]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-lg font-medium text-white/80">
            {contact?.name?.[0]?.toUpperCase() || conversation.contact_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <h3 className="font-semibold text-white">
              {contact?.name || conversation.contact_name || "Unknown"}
            </h3>
            <div className="flex items-center gap-1 mt-0.5">
              <ChannelBadge channel={conversation.channel} />
              <span className="text-xs text-white/50">
                {CHANNELS[conversation.channel]?.label || conversation.channel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Phone */}
        {contact?.phone && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white/50" />
            </div>
            <div>
              <p className="text-xs text-white/50">Phone</p>
              <p className="text-sm text-white">{contact.phone}</p>
            </div>
          </div>
        )}

        {/* Email */}
        {contact?.email && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
              <Mail className="w-4 h-4 text-white/50" />
            </div>
            <div>
              <p className="text-xs text-white/50">Email</p>
              <p className="text-sm text-white">{contact.email}</p>
            </div>
          </div>
        )}

        {/* Tags */}
        {contact?.tags?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/50">Tags</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {contact.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-xs bg-white/10 text-white/70 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <StickyNote className="w-4 h-4 text-white/50" />
            <span className="text-xs text-white/50">Notes</span>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this contact..."
            className="w-full h-24 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
          />
          {notes !== (contact?.notes || "") && (
            <button
              onClick={handleSaveNotes}
              disabled={isSaving}
              className="mt-2 px-3 py-1.5 text-xs bg-[#ff4d4d] hover:bg-[#ff6b6b] text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
            >
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save Notes
            </button>
          )}
        </div>

        {/* Lead Link */}
        {leadLink && (
          <div className="pt-4 border-t border-white/10">
            <button
              onClick={() => {/* Navigate to lead */}}
              className="w-full flex items-center justify-between p-3 bg-[#ff4d4d]/10 rounded-lg text-[#ff4d4d] hover:bg-[#ff4d4d]/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">View Lead Card</span>
              </div>
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Chat Component - Claw9 red theme
const Claw9Chat = ({ userEmail, initialChannel = "all", onOpenMessagingChannels }) => {
  // Account state - fetched dynamically for the logged-in user
  const [accountId, setAccountId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [accountError, setAccountError] = useState(null);

  // Filter state
  const [channelFilter, setChannelFilter] = useState(initialChannel || "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [conversationsCursor, setConversationsCursor] = useState(null);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);

  // Messages state
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  // Compose state
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTogglingAI, setIsTogglingAI] = useState(false);
  const [aiActiveOverride, setAiActiveOverride] = useState(null); // Local override for immediate UI feedback

  // Contact & Lead state
  const [contactInfo, setContactInfo] = useState(null);
  const [leadLink, setLeadLink] = useState(null);

  // Mobile state
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const localMessageIdsRef = useRef(new Set()); // Track locally sent messages to prevent duplicates
  const lastServerFetchRef = useRef(null); // Track last server fetch time to prevent stale data overwrites
  const isUserScrollingRef = useRef(false); // Track if user has scrolled away from bottom

  useEffect(() => {
    if (initialChannel) {
      setChannelFilter(initialChannel);
    }
  }, [initialChannel]);

  // Load conversations
  const loadConversations = useCallback(async (reset = false) => {
    if (!accountId) {
      return;
    }

    try {
      if (reset) {
        setIsLoadingConversations(true);
        setConversationsCursor(null);
      }

      const params = {
        accountId,
        channel: channelFilter,
        status: statusFilter,
        searchName: searchTerm,
        cursor: reset ? undefined : conversationsCursor,
      };

      const result = await fetchClaw9Conversations(params);
      const convos = result.conversations || result.data?.conversations || result.data || [];
      const convoArray = Array.isArray(convos) ? convos : [];

      if (reset) {
        setConversations(convoArray);
      } else {
        setConversations((prev) => [...prev, ...convoArray]);
      }

      setConversationsCursor(result.cursor || result.next_cursor);
      setHasMoreConversations(result.hasMore || result.has_more || false);
    } catch (error) {
      console.error("[Claw9Chat] Failed to load conversations:", error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [accountId, channelFilter, statusFilter, searchTerm, conversationsCursor]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (conversation, reset = false) => {
    if (!conversation || !accountId) return;

    const threadId = conversation.thread_id || conversation.id || conversation.conversation_id;
    if (!threadId) return;

    try {
      if (reset) {
        setIsLoadingMessages(true);
        setMessagesCursor(null);
      }

      const result = await fetchClaw9Messages(
        conversation.channel,
        threadId,
        { accountId, cursor: reset ? undefined : messagesCursor }
      );

      const newMessages = result.messages || result.data?.messages || result.data || [];
      const messagesArray = Array.isArray(newMessages) ? newMessages : [];

      // Sort messages by created_at (oldest first for chat display)
      const sortedMessages = [...messagesArray].sort((a, b) => {
        const timeA = new Date(a.created_at || 0).getTime();
        const timeB = new Date(b.created_at || 0).getTime();
        return timeA - timeB; // Ascending: oldest first
      });

      if (reset) {
        setMessages(sortedMessages);
        markClaw9ConversationRead(conversation.channel, threadId, accountId);

        // Sync last message to conversation list
        if (sortedMessages.length > 0) {
          const latestMsg = sortedMessages[sortedMessages.length - 1];
          setConversations((prevConvs) =>
            prevConvs.map((conv) =>
              (conv.thread_id === threadId || conv.id === threadId)
                ? { ...conv, last_message: latestMsg.content, last_message_at: latestMsg.created_at }
                : conv
            )
          );
        }
      } else {
        setMessages((prev) => [...sortedMessages, ...prev]);
      }

      setMessagesCursor(result.cursor);
      setHasMoreMessages(result.hasMore || false);
    } catch (error) {
      console.error("Failed to load messages:", error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [accountId, messagesCursor]);

  // Fetch user's accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        setAccountError(null);
        const result = await fetchClaw9Accounts();
        const userAccounts = result.accounts || [];
        setAccounts(userAccounts);

        if (userAccounts.length > 0) {
          // Use the first account by default
          setAccountId(userAccounts[0].account_id);
        } else {
          // No accounts found - use default fallback
          setAccountId('claw9care');
        }
      } catch (error) {
        console.error('[Claw9Chat] Failed to load accounts:', error);
        // API not available or error - use default fallback
        setAccountId('claw9care');
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    loadAccounts();
  }, []);

  // Initial load + polling for conversation list every 5 seconds
  useEffect(() => {
    if (!accountId) return; // Don't start until account is loaded

    loadConversations(true);

    // Polling for conversations - silently refresh
    let pollingInterval = null;

    const pollConversations = async () => {
      try {
        const params = {
          accountId,
          channel: channelFilter,
          status: statusFilter,
          searchName: searchTerm,
        };
        const result = await fetchClaw9Conversations(params);
        const convos = result.conversations || result.data?.conversations || result.data || [];
        if (Array.isArray(convos)) {
          setConversations(convos);
        }
      } catch (error) {
        // Silent fail on polling
      }
    };

    // Only poll when tab is visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingInterval) clearInterval(pollingInterval);
        pollingInterval = null;
      } else {
        pollConversations();
        pollingInterval = setInterval(pollConversations, 5000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    pollingInterval = setInterval(pollConversations, 5000);

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accountId, channelFilter, statusFilter, searchTerm]);

  // Auto-select first conversation when loaded and none selected
  useEffect(() => {
    if (conversations.length > 0 && !selectedConversation && !isLoadingConversations) {
      setSelectedConversation(conversations[0]);
      setShowMobileChat(true);
    }
  }, [conversations, selectedConversation, isLoadingConversations]);

  // Load messages when conversation changes + polling every 3 seconds
  useEffect(() => {
    let pollingInterval = null;

    if (selectedConversation && accountId) {
      loadMessages(selectedConversation, true);
      lookupClaw9LeadByContact(
        accountId,
        selectedConversation.channel,
        selectedConversation.contact_id
      ).then(setLeadLink);

      // Poll messages every 3 seconds - silently refresh with SMART MERGE
      const pollMessages = async () => {
        const threadId = selectedConversation.thread_id || selectedConversation.id;
        if (!threadId) return;

        try {
          const result = await fetchClaw9Messages(
            selectedConversation.channel,
            threadId,
            { accountId }
          );
          const serverMessages = result.messages || result.data?.messages || result.data || [];
          if (Array.isArray(serverMessages) && serverMessages.length > 0) {
            // Sort messages by created_at (oldest first for chat display)
            const sortedServerMessages = [...serverMessages].sort((a, b) => {
              const timeA = new Date(a.created_at || 0).getTime();
              const timeB = new Date(b.created_at || 0).getTime();
              return timeA - timeB; // Ascending: oldest first
            });

            // SMART MERGE: Preserve local messages and only add truly new ones
            setMessages(prevMessages => {
              // Keep any temp/sending messages (optimistic updates)
              const tempMessages = prevMessages.filter(msg =>
                msg.id?.startsWith('temp-') || msg.isTemp || msg.status === 'sending'
              );

              // Get IDs of existing non-temp messages
              const existingIds = new Set(prevMessages.filter(m => !m.id?.startsWith('temp-')).map(m => m.id));

              // Find truly new messages from server
              const newFromServer = sortedServerMessages.filter(msg => !existingIds.has(msg.id));

              // If no new messages, just update existing ones (for edits/reactions)
              if (newFromServer.length === 0 && tempMessages.length === 0) {
                // Return sorted server messages but preserve any local modifications
                return sortedServerMessages;
              }

              // Merge: server messages + temp messages, deduplicated
              const serverIds = new Set(sortedServerMessages.map(m => m.id));
              const remainingTempMessages = tempMessages.filter(m => !serverIds.has(m.id?.replace('temp-', '')));

              const merged = [...sortedServerMessages, ...remainingTempMessages];
              return merged.sort((a, b) => {
                const timeA = new Date(a.created_at || 0).getTime();
                const timeB = new Date(b.created_at || 0).getTime();
                return timeA - timeB;
              });
            });

            // Sync the last message to the conversation list (left panel)
            const latestMsg = sortedServerMessages[sortedServerMessages.length - 1];
            if (latestMsg) {
              setConversations((prevConvs) =>
                prevConvs.map((conv) =>
                  (conv.thread_id === threadId || conv.id === threadId)
                    ? {
                        ...conv,
                        last_message: latestMsg.content,
                        last_message_at: latestMsg.created_at,
                      }
                    : conv
                )
              );
              // Also update selectedConversation
              setSelectedConversation((prev) =>
                prev ? { ...prev, last_message: latestMsg.content, last_message_at: latestMsg.created_at } : prev
              );
            }
          }
        } catch (error) {
          // Silent fail on polling
        }
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          if (pollingInterval) clearInterval(pollingInterval);
          pollingInterval = null;
        } else {
          pollMessages();
          pollingInterval = setInterval(pollMessages, 3000);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      pollingInterval = setInterval(pollMessages, 3000);

      return () => {
        if (pollingInterval) clearInterval(pollingInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      setMessages([]);
      setLeadLink(null);
    }

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [selectedConversation, accountId]);

  // Check if user is near the bottom of the messages container
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Handle scroll events to track user scrolling
  const handleMessagesScroll = useCallback(() => {
    isUserScrollingRef.current = !isNearBottom();
  }, [isNearBottom]);

  // Scroll to bottom - only if user hasn't scrolled away
  const scrollToBottom = useCallback((force = false) => {
    if (force || !isUserScrollingRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      isUserScrollingRef.current = false;
    }
  }, []);

  // Scroll to bottom on new messages only if user is at bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || isSending) return;

    const content = newMessage.trim();
    setNewMessage("");
    setIsSending(true);

    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      content,
      sender_type: "agent",
      sender_name: userEmail?.split("@")[0] || "Agent",
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMsg]);

    // Force scroll to bottom when user sends a message
    setTimeout(() => scrollToBottom(true), 50);

    try {
      const sent = await sendClaw9Message(
        selectedConversation.channel,
        selectedConversation.thread_id,
        accountId,
        content
      );

      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticMsg.id ? sent : m))
      );
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      setNewMessage(content);
    } finally {
      setIsSending(false);
      messageInputRef.current?.focus();
    }
  };

  // Handle AI toggle - UI only, no backend call
  const handleToggleAI = () => {
    if (!selectedConversation) return;

    const currentlyActive = aiActiveOverride !== null ? aiActiveOverride : isAIActive(messages);
    const newActiveState = !currentlyActive;

    // Just toggle the UI state - no API call
    setAiActiveOverride(newActiveState);
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!selectedConversation) return;

    try {
      await updateClaw9Conversation(
        selectedConversation.channel,
        selectedConversation.thread_id,
        accountId,
        { status: newStatus }
      );

      setSelectedConversation({ ...selectedConversation, status: newStatus });
      setConversations((prev) =>
        prev.map((c) =>
          c.thread_id === selectedConversation.thread_id
            ? { ...c, status: newStatus }
            : c
        )
      );
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  // Handle notes update
  const handleUpdateNotes = async (notes) => {
    if (!selectedConversation) return;

    try {
      await updateClaw9Contact(
        selectedConversation.channel,
        selectedConversation.contact_id,
        accountId,
        { notes }
      );
      setContactInfo((prev) => (prev ? { ...prev, notes } : null));
    } catch (error) {
      console.error("Failed to update notes:", error);
    }
  };

  // Select conversation
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowMobileChat(true);
    setContactInfo(conv.contact || null);
    setAiActiveOverride(null); // Reset AI override when switching conversations
    localMessageIdsRef.current = new Set(); // Clear local message tracking
    lastServerFetchRef.current = null; // Reset server fetch tracking
  };

  // Show loading state while fetching accounts
  if (isLoadingAccounts) {
    return (
      <div className="flex flex-1 min-h-0 h-full bg-[#0a0f1c] border border-white/10 rounded-xl overflow-hidden shadow-2xl items-center justify-center">
        <div className="flex flex-col items-center text-white/50">
          <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d] mb-3" />
          <p className="text-sm">Loading accounts...</p>
        </div>
      </div>
    );
  }

  // Show error state if accounts failed to load
  if (accountError) {
    return (
      <div className="flex flex-1 min-h-0 h-full bg-[#0a0f1c] border border-white/10 rounded-xl overflow-hidden shadow-2xl items-center justify-center">
        <div className="flex flex-col items-center text-white/50 max-w-md text-center px-4">
          <XCircle className="w-10 h-10 text-red-500 mb-3" />
          <p className="text-lg font-medium text-white mb-2">Unable to Load Chat</p>
          <p className="text-sm mb-4">{accountError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff6b6b] text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 h-full bg-[#0a0f1c] border border-white/10 rounded-xl overflow-hidden shadow-2xl relative">
      {/* Left Panel - Conversation List */}
      <div
        className={`w-full md:w-80 lg:w-96 flex-shrink-0 bg-[#0d1424] border-r border-white/10 flex flex-col ${
          showMobileChat ? "hidden md:flex" : "flex"
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <h1 className="text-lg font-semibold text-white mb-3">
            Chat
          </h1>

          {/* Channel Tabs */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
            {Object.entries(CHANNELS).filter(([key]) => key !== "facebook").map(([key, config]) => (
              <button
                key={key}
                onClick={() => setChannelFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  channelFilter === key
                    ? "bg-[#ff4d4d] text-white"
                    : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === "all"
                  ? "bg-white text-[#0a0f1c]"
                  : "text-white/60 hover:bg-white/5"
              }`}
            >
              All
            </button>
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                  statusFilter === opt.value
                    ? "bg-white text-[#0a0f1c]"
                    : "text-white/60 hover:bg-white/5"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/40"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoadingConversations && conversations.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#ff4d4d]" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-white/50">
              <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No conversations found</p>
              {(channelFilter === "messenger" || channelFilter === "instagram") && userEmail === 'andrew@claw9.ai' && (
                <button
                  onClick={() => onOpenMessagingChannels?.()}
                  className="mt-3 px-3 py-1.5 rounded-lg bg-[#ff4d4d]/15 text-[#ff8080] hover:bg-[#ff4d4d]/25 text-xs font-medium transition-colors"
                >
                  Connect {getChannelLabel(channelFilter)}
                </button>
              )}
            </div>
          ) : (
            <>
              {conversations.map((conv) => (
                <ConversationItem
                  key={`${conv.channel}-${conv.thread_id}`}
                  conversation={conv}
                  isSelected={
                    selectedConversation?.thread_id === conv.thread_id &&
                    selectedConversation?.channel === conv.channel
                  }
                  onClick={() => handleSelectConversation(conv)}
                />
              ))}

              {hasMoreConversations && (
                <button
                  onClick={() => loadConversations(false)}
                  disabled={isLoadingConversations}
                  className="w-full py-3 text-sm text-[#ff4d4d] hover:bg-white/5 disabled:opacity-50"
                >
                  {isLoadingConversations ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    "Load more"
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Center Panel - Chat Window */}
      <div
        className={`flex-1 flex flex-col bg-[#050810] ${
          !showMobileChat ? "hidden md:flex" : "flex"
        }`}
      >
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowMobileChat(false)}
                  className="md:hidden p-1 -ml-1 text-white/50 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-white/80 font-medium">
                    {selectedConversation.contact_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5">
                    <ChannelBadge channel={selectedConversation.channel} />
                  </div>
                </div>

                <div>
                  <h2 className="font-semibold text-white">
                    {selectedConversation.contact_name || "Unknown"}
                  </h2>
                  <p className="text-xs text-white/50">
                    {CHANNELS[selectedConversation.channel]?.label || selectedConversation.channel}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* AI/Human Status Toggle */}
                <AIStatusToggle
                  isActive={aiActiveOverride !== null ? aiActiveOverride : isAIActive(messages)}
                  onToggle={handleToggleAI}
                  isToggling={isTogglingAI}
                />

                <select
                  value={selectedConversation.status || "open"}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#0a0f1c]">
                      {opt.label}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                  className="lg:hidden p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <User className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              className="flex-1 min-h-0 overflow-y-auto p-4"
            >
              {hasMoreMessages && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={() => loadMessages(selectedConversation, false)}
                    disabled={isLoadingMessages}
                    className="px-3 py-1.5 text-xs text-[#ff4d4d] bg-[#ff4d4d]/10 rounded-full hover:bg-[#ff4d4d]/20 disabled:opacity-50"
                  >
                    {isLoadingMessages ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Load older messages"
                    )}
                  </button>
                </div>
              )}

              {isLoadingMessages && messages.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-[#ff4d4d]" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-white/50">
                  <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isAgent={msg.sender_type === "agent" || msg.sender_type === "bot"}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-end gap-2">
                <textarea
                  ref={messageInputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 px-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/40"
                  style={{ maxHeight: "120px" }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending}
                  className="p-2.5 bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:shadow-lg hover:shadow-[#ff4d4d]/30"
                >
                  {isSending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-white/50">
            <MessageSquare className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose a conversation from the list to start chatting</p>
          </div>
        )}
      </div>

      {/* Right Panel - Contact Sidebar (Desktop) */}
      <div className="hidden lg:flex w-72 xl:w-80 flex-shrink-0 border-l border-white/10">
        <ContactSidebar
          conversation={selectedConversation}
          contact={contactInfo}
          leadLink={leadLink}
          onUpdateNotes={handleUpdateNotes}
        />
      </div>

      {/* Mobile Contact Sidebar Overlay */}
      {showMobileSidebar && selectedConversation && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowMobileSidebar(false)}
          />
          <div className="fixed inset-y-0 right-0 w-80 z-50 lg:hidden">
            <ContactSidebar
              conversation={selectedConversation}
              contact={contactInfo}
              leadLink={leadLink}
              onUpdateNotes={handleUpdateNotes}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Claw9Chat;
